import { boolean, object, string } from 'yup'

const ContactUsSchema = object().shape({
  form_name: string().required(),
  title: string()
    .required('Title is required')
    .min(1, 'Title must be at least 2 characters')
    .max(10, 'Title must be at most 10 characters')
    .trim(),
  first_name: string()
    .required('First name is required')
    .matches(
      /^[A-Za-z-.,'` ]{1,50}$/,
      'First name can only contain letters and basic punctuation',
    )
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters')
    .trim(),
  last_name: string()
    .required('Last name is required')
    .matches(
      /^[A-Za-z-.,'` ]{1,50}$/,
      'Last name can only contain letters and basic punctuation',
    )
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters')
    .trim(),
  email: string()
    .required('Email is required')
    .matches(
      /^([a-z0-9]+(?:[._-][a-z0-9]+)*)@([a-z0-9]+(?:[.-][a-z0-9]+)*\.[a-z]{2,})$/,
      'Email must be a valid email address',
    )
    .max(255)
    .trim(),
  month: string().when('form_name', {
    is: (value) => ['financial-services'].includes(value),
    then: (rule) => rule.trim().matches(/0[1-9]|1[0-2]/, 'Month is required'),
  }),
  day: string()
    .when('form_name', {
      is: (value) => ['financial-services'].includes(value),
      then: (rule) =>
        rule
          .trim()
          .matches(/^(?:^0[1-9]|[1-2][0-9]|30|31)$/, 'Day is required'),
    })
    .when('month', {
      is: (value) => ['04', '06', '09', '11'].includes(value),
      then: (rule) =>
        rule
          .trim()
          .matches(/^(?:^0[1-9]|[1-2][0-9]|30)$/, 'Day is invalid')
          .required('Day is required'),
    })
    .when('month', {
      is: (value) => ['02'].includes(value),
      then: (rule) =>
        rule
          .trim()
          .matches(/^(?:^0[1-9]|1[0-9]|2[0-8]|29)$/, 'Day is invalid')
          .required('Day is required'),
    })
    .when('month', {
      is: (value) => ['01, 03, 05, 07, 08, 10, 12'].includes(value),
      then: (rule) =>
        rule
          .trim()
          .matches(/^(?:^0[1-9]|[1-2][0-9]|30|31)$/, 'Day is invalid')
          .required('Day is required'),
    }),

  year: string().when('form_name', {
    is: (value) => ['financial-services'].includes(value),
    then: (rule) =>
      rule
        .trim()
        .matches(/^(192[4-9]|19[3-9]\d|200[0-5])$/, 'Year is required'),
  }),
  agreement_or_reg_num: string().when('form_name', {
    is: (value) => ['financial-services'].includes(value),
    then: (rule) =>
      rule
        .trim()
        .matches(
          /(^[a-zA-Z0-9]{1,4}\s?[a-zA-Z0-9]{1,3}$)|(^[\w]{1,12}$)/,
          'Please insert a valid Agreement number or Vehicle registration',
        ),
  }),
  urn: string().when('form_name', {
    is: (value) => ['roadside-assistance'].includes(value),
    then: (rule) =>
      rule.trim().matches(/^[\d]{1,10}$/, 'Membership number is required'),
  }),
  type_of_enquiry: string().when('form_name', {
    is: (value) => !['fleet-services'].includes(value),
    then: (rule) => rule.required('Type of enquiry is required').trim(),
  }),
  address_line1: string().trim(),
  address_line2: string().trim(),
  town: string().trim(),
  county: string().trim(),
  postcode: string().when('form_name', {
    is: (value) => ['financial-services', 'fleet-services'].includes(value),
    then: (rule) =>
      rule
        .trim()
        .matches(
          /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]?[0-9][A-Za-z]{2}|(E|N|W|S|NW|SE|SW|EC|WC)[0-9Rr][0-9A-Za-z]?[\s]?[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2})$/,
          'Postcode must be a valid UK postcode',
        )
        .required('Postcode is required'),
  }),
  tel: string().when('form_name', {
    is: (value) => ['financial-services', 'fleet-services'].includes(value),
    then: (rule) =>
      rule
        .required('Please enter a telephone number')
        .matches(/^\d{7,11}$/, {
          message: 'Please enter a valid telephone number',
          excludeEmptyString: true,
        })
        .trim(),
  }),
  mobile_number: string().trim(),
  additional_info: string().trim(),
  company_name: string().when('form_name', {
    is: (value) => ['fleet-services'].includes(value),
    then: (rule) => rule.required('Company name is required').trim(),
  }),
  enquiry_description: string().when('form_name', {
    is: (value) => [!'fleet-services'].includes(value),
    then: (rule) => rule.required().trim(),
  }),
  dealer_name: string().trim(),
  dealer_id: string().trim(),
  reg_num: string().when('form_name', {
    is: (value) => ['roadside-assistance', 'multimedia'].includes(value),
    then: (rule) =>
      rule
        .trim()
        .matches(
          /(^[a-zA-Z]{2}[0-9]{2}\s?[a-zA-Z]{3}$)|(^[a-zA-Z][0-9]{1,3}[a-zA-Z]{3}$)|(^[a-zA-Z]{3}[0-9]{1,3}[a-zA-Z]$)|(^[0-9]{1,4}[a-zA-Z]{1,2}$)|(^[0-9]{1,3}[a-zA-Z]{1,3}$)|(^[a-zA-Z]{1,2}[0-9]{1,4}$)|(^[a-zA-Z]{1,3}[0-9]{1,3}$)|(^[a-zA-Z]{1,3}[0-9]{1,4}$)|(^[0-9]{3}[DX]{1}[0-9]{3}$)/,
          'Registration number is invalid',
        ),
  }),
  fleet_type: string().trim(),
  is_lexus: boolean(),
})

export default ContactUsSchema
